// ExpenseLogDialog.js

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import axios from "axios";
import moment from "moment";
import querystring from "querystring";
import { translator } from "../../utils/Translator";

const getActionText = (actionType, translate) => {
  switch (actionType) {
    case 'A':
      return translator(translate.ISA_CreateDocButton, 'สร้างเอกสาร');
    case 'U':
      return translator(translate.ISA_AUTO_UPDATE_DOC, 'อัปเดตเอกสาร');
    case 'D':
      return translator(translate.ISA_DeleteDocument, 'ลบเอกสาร');
    case 'E':
      return translator(translate.ISA_AUTO_EDIT_DOC, 'แก้ไขเอกสาร');
    default:
      return translator(translate.ISA_Action, 'ดำเนินการ'); // ค่าสำรอง
  }
};// 1. Import querystring เข้ามา

// Component สำหรับแสดงแถวข้อมูล Log
const LogRow = ({ log }) => {
  // ตัด "Z" ทิ้ง เพื่อไม่ให้ JS คิดว่าเป็น UTC
  const dateStr = log.TimeStamp.replace("Z", "");

  const date = new Date(dateStr); // จะตีความเป็น Local เวลา 13:35

  const formatted = new Intl.DateTimeFormat("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);

  return (
    <tr>
      <td>{formatted}</td>
      <td>{log.Action}</td>
      <td>{log.User}</td>
    </tr>
  );
};


// 2. เพิ่มการรับ companyCode จาก props
const StockCountLogDialog = ({ open, onClose, docNo, companyCode }) => {
  const { translate } = useSelector(({ i18n }) => i18n);
  const [logs, setLogs] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
    
  

  useEffect(() => {
    const fetchLogs = async () => {
      // เพิ่มการตรวจสอบ companyCode ด้วย
      if (!open || !docNo || !companyCode) {
        return;
      }
      setLoading(true);
      setError(null);
      setLogs([]);

      try {
        // const companyCode = companyCode; // คุณต้องส่งค่า company ที่ถูกต้องมา

        // สร้าง object params
        const params = {
          docId: docNo,
          company: companyCode,
        };

        // ใช้ querystring เพื่อสร้าง query string ที่ถูกต้อง
        const queryString = querystring.stringify(params);

        // --- [แก้ไข] สร้าง URL ให้ถูกต้อง ---
        // Path คือ '/api/CustomerAccount/GetAuditLog' และต่อด้วย query string
        const url = `${process.env.REACT_APP_API_URL}CustomerAccount/GetAuditLog?${queryString}`;

        // เรียก API
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("x-access-token")}`,
          },
        });

        // 6. Mapping ข้อมูล (ส่วนนี้ยังคงจำเป็น)
        // ตรวจสอบให้แน่ใจว่า response.data เป็น array
        if (response.data.data && Array.isArray(response.data.data)) {
          const mappedLogs = response.data.data.map((log) => ({
            TimeStamp: log.CreatedAt,
            Action: getActionText(log.ActionType, translate),
            User: log.UserID,
          }));
          setLogs(mappedLogs);

        } else {
          console.warn(
            " No log data returned from API or data is not an array"
          );
          setLogs([]);
        }
      } 
      catch (err) {
        setError(translator(translate.ISA_CannotLoadHistory, "ไม่สามารถดึงข้อมูลประวัติการแก้ไขได้"));
        console.error(
          " Failed to fetch audit logs:",
          err.response || err.message
        );
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [open, docNo, companyCode]); // 7. เพิ่ม companyCode ใน dependency array

  const loadingText = translator(translate.ISA_Loading, "กำลังโหลด...");
  const noHistoryText = translator(translate.ISA_NoHistoryData, "ไม่มีข้อมูลประวัติ");
  const closeText = translator(translate.ISA_Status_Closed, "ปิด");
  const logFileText = translator(translate.ISA_LogFile, "Log File");
  const documentHistoryText = translator(translate.ISA_DocumentHistory, "ประวัติการแก้ไขเอกสาร");
  const timeStampText = translator(translate.ISA_TIME_STAMP, "Time Stamp");
  const actionText = translator(translate.ISA_Action, "การดำเนินการ");
  const editedByText = translator(translate.ISA_Modifier, "ผู้แก้ไข");

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {logFileText} - {documentHistoryText} {docNo}
      </DialogTitle>
      <DialogContent>
        <style>{`
            .log-table { width: 100%; border-collapse: collapse; font-family: sans-serif; }
            .log-table th, .log-table td { border-bottom: 1px solid #e0e0e0; padding: 12px 16px; text-align: left; vertical-align: top; }
            .log-table th { font-weight: 600; background-color: #f5f5f5; }
            .log-table td div { line-height: 1.4; }
            .loading-text, .error-text, .no-data-text { text-align: center; padding: 20px; color: #888; }
        `}</style>
        {loading && <div className="loading-text">{loadingText}</div>}
        {error && <div className="error-text">{error}</div>}
        {!loading && !error && logs.length > 0 && (
          <table className="log-table">
            <thead>
              <tr>
                <th>{timeStampText}</th>
                <th>{actionText}</th>
                <th>{editedByText}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, index) => (
                <LogRow key={index} log={log} />
              ))}
            </tbody>
          </table>
        )}
        {!loading && !error && logs.length === 0 && (
          <div className="no-data-text">{noHistoryText}</div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {closeText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


export default StockCountLogDialog;
